import { useCallback, useEffect, useState } from 'react';

import type { GlobalSearchItem } from './use-global-search';

// Recent items: últimos 5 resultados clickeados desde el palette. Persistido
// en localStorage para sobrevivir reloads. Único cliente del store, así que
// no usamos zustand — sólo un hook con `storage` event listener.

const STORAGE_KEY = 'simplificalo:recent-search';
const MAX_ITEMS = 5;

function read(): readonly GlobalSearchItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as readonly GlobalSearchItem[];
  } catch {
    return [];
  }
}

function write(items: readonly GlobalSearchItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    // localStorage puede fallar en modo privado / quota — fail-silent
  }
}

export function useRecentItems() {
  const [items, setItems] = useState<readonly GlobalSearchItem[]>(() => read());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setItems(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const push = useCallback((item: GlobalSearchItem) => {
    const next = [item, ...read().filter((p) => !(p.type === item.type && p.id === item.id))];
    write(next);
    setItems(next.slice(0, MAX_ITEMS));
  }, []);

  return { items, push };
}
